import { FC } from 'react';

import { Typography, Stack, Badge, styled, BadgeProps } from '@mui/material';

import parse from 'html-react-parser';
import { makeStyles } from 'tss-react/mui';

import Accordion from '@/components/accordion';
import { useCreatorPostVisuals } from '@/contexts/CreatorDetailsContext';
import { Posts } from '@/models/posts';
import { msToPrettyTimestamp, msToSeconds } from '@/utils/datesAndTime';

const useStyles = makeStyles()((theme) => ({
  highlighted: {
    background: theme.palette.grey[100],
    borderLeft: `2px solid ${theme.palette.primary.main}`,
    paddingLeft: 5,
    cursor: 'pointer'
  },
  notHighlighted: {
    cursor: 'pointer'
  }
}));

type OcrClasses = ReturnType<typeof useStyles>['classes'];

const StyledBadge = styled(Badge)<BadgeProps>(() => ({
  '& .MuiBadge-badge': {
    right: -15,
    top: 12
  }
}));

export interface MediaItemBadgeProps {
  index: number;
  activeIndex: number;
  itemCount: number;
}

export const MediaItemBadge: FC<MediaItemBadgeProps> = ({
  index,
  activeIndex,
  itemCount
}) => {
  const isSelected = activeIndex === index;

  return (
    <StyledBadge badgeContent={itemCount} color="primary">
      <Typography
        variant="body2"
        sx={{
          transition: 'all 0.15s ease',
          ...(isSelected && {
            fontWeight: 600,
            textDecoration: 'underline'
          })
        }}
      >
        {`Item ${index + 1}`}
      </Typography>
    </StyledBadge>
  );
};

const isSameCoordinates = (
  a: Posts.OcrAdvImageItem['coordinates'],
  b: Posts.OcrAdvImageItem['coordinates']
) => JSON.stringify(a) === JSON.stringify(b);

const isVideoItem = (
  item: Posts.OcrAdvVideoItem | Posts.OcrAdvImageItem
): item is Posts.OcrAdvVideoItem => 'timestamp' in item;

const getOcrAdvancedItemsForMedia = (mediaItem: Posts.Media) =>
  mediaItem.visualRecognition
    ?.filter(
      (
        item
      ): item is Posts.Recognition & {
        type: 'ocr-adv';
        items: (Posts.OcrAdvVideoItem | Posts.OcrAdvImageItem)[];
      } => item.type === 'ocr-adv'
    )
    .flatMap((item) => item.items) ?? [];

const getOcrOldTextForMedia = (mediaItem: Posts.Media) => {
  const texts: string[] = [];
  mediaItem.visualRecognition?.forEach((recognition) => {
    if (recognition.type === 'ocr' && recognition.items?.length > 0) {
      recognition.items.forEach((item: any) => {
        if (item.text) texts.push(item.text);
      });
    }
  });
  return texts.join(' -- ');
};

const getOcrItemCountForMedia = (mediaItem: Posts.Media) => {
  const advancedItems = getOcrAdvancedItemsForMedia(mediaItem);
  if (advancedItems.length > 0) return advancedItems.length;

  const oldText = getOcrOldTextForMedia(mediaItem);
  return oldText ? oldText.split('--').filter(Boolean).length : 0;
};

const VideoLine: FC<{
  item: Posts.OcrAdvVideoItem;
  isSelected: boolean;
  onClick: (item: Posts.OcrAdvVideoItem) => void;
  classes: OcrClasses;
}> = ({ item, isSelected, onClick, classes }) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={2}
    padding={0.25}
    px={1}
    className={isSelected ? classes.highlighted : classes.notHighlighted}
    onClick={() => onClick(item)}
  >
    <Typography color="primary" fontSize={12} width={35} fontWeight={500}>
      {msToPrettyTimestamp(item.timestamp)}
    </Typography>
    <Typography variant="body2" fontSize={12}>
      {item.textFound.map((text, index) => (
        <span key={index}>{parse(text.content)} </span>
      ))}
    </Typography>
  </Stack>
);

const ImageLine: FC<{
  item: Posts.OcrAdvImageItem;
  isSelected: boolean;
  onClick: (item: Posts.OcrAdvImageItem) => void;
  classes: OcrClasses;
}> = ({ item, isSelected, onClick, classes }) => (
  <Stack
    padding={0.25}
    px={1}
    className={isSelected ? classes.highlighted : classes.notHighlighted}
    onClick={() => onClick(item)}
  >
    <Typography variant="body2" fontSize={12}>
      {parse(item.content)}
    </Typography>
  </Stack>
);

interface CreatorPostOcrProps {
  media: Posts.Media[];
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  setVideoPlaybackStart?: (seconds: number) => void;
  setVideoLastClick?: (ts: number) => void;
}

const CreatorPostOcr: FC<CreatorPostOcrProps> = ({
  media,
  activeIndex,
  setActiveIndex,
  setVideoPlaybackStart,
  setVideoLastClick
}) => {
  const { classes } = useStyles();
  const { selectedOcrAdvItems, setSelectedOcrAdvItems, clearSelections } =
    useCreatorPostVisuals();

  const hasOcrAdvancedItems = media.some(
    (m) => getOcrAdvancedItemsForMedia(m).length > 0
  );
  const hasOcrOldItems = media.some((m) => !!getOcrOldTextForMedia(m));

  const isVideoItemSelected = (item: Posts.OcrAdvVideoItem) =>
    selectedOcrAdvItems.some(
      (selected: any) => selected.timestamp === item.timestamp
    );

  const isImageItemSelected = (item: Posts.OcrAdvImageItem) =>
    selectedOcrAdvItems.some((selected: any) =>
      isSameCoordinates(selected.coordinates, item.coordinates)
    );

  const handleVideoItemClick = (item: Posts.OcrAdvVideoItem) => {
    if (isVideoItemSelected(item)) {
      setSelectedOcrAdvItems([]);
      return;
    }

    setSelectedOcrAdvItems(
      item.textFound.map((text) => ({ ...text, timestamp: item.timestamp }))
    );
    setVideoPlaybackStart?.(msToSeconds(item.timestamp));
    setVideoLastClick?.(Date.now());
  };

  const handleImageItemClick = (item: Posts.OcrAdvImageItem) => {
    setSelectedOcrAdvItems(
      isImageItemSelected(item)
        ? selectedOcrAdvItems.filter(
            (selected: any) =>
              !isSameCoordinates(selected.coordinates, item.coordinates)
          )
        : [...selectedOcrAdvItems, item]
    );
  };

  const handleAccordionChange = (index: number) => {
    setActiveIndex(index);
    clearSelections();
  };

  const renderMediaOcrContent = (mediaItem: Posts.Media) => {
    const advancedItems = getOcrAdvancedItemsForMedia(mediaItem);

    if (advancedItems.length > 0) {
      return (
        <Stack spacing={0.5}>
          {advancedItems.map((item, index) =>
            isVideoItem(item) ? (
              <VideoLine
                key={index}
                item={item}
                isSelected={isVideoItemSelected(item)}
                onClick={handleVideoItemClick}
                classes={classes}
              />
            ) : (
              <ImageLine
                key={index}
                item={item}
                isSelected={isImageItemSelected(item)}
                onClick={handleImageItemClick}
                classes={classes}
              />
            )
          )}
        </Stack>
      );
    }

    const oldText = getOcrOldTextForMedia(mediaItem);
    if (oldText) {
      return (
        <Stack sx={{ overflow: 'auto', maxHeight: 240 }}>
          {oldText.split('--').map((segment, index) => (
            <Typography key={index} variant="caption" color="textSecondary">
              {parse(segment)}
            </Typography>
          ))}
        </Stack>
      );
    }

    return null;
  };

  if (!hasOcrAdvancedItems && !hasOcrOldItems) return null;

  if (media.length === 1) return renderMediaOcrContent(media[0]);

  return (
    <Stack>
      {media.map((mediaItem, index) => (
        <Accordion
          key={mediaItem.mediaPath}
          id={mediaItem.mediaPath}
          title={
            <MediaItemBadge
              index={index}
              activeIndex={activeIndex}
              itemCount={getOcrItemCountForMedia(mediaItem)}
            />
          }
          handleChange={() => handleAccordionChange(index)}
          isExpanded={index === activeIndex}
          condensed
        >
          {renderMediaOcrContent(mediaItem)}
        </Accordion>
      ))}
    </Stack>
  );
};

export default CreatorPostOcr;
